import React, { useCallback, useEffect, useRef, useState } from 'react';

import { App, Button, Form, Input, Modal, Result, Select, Spin, Steps, Switch, Typography } from 'antd';

import { constants } from 'helpers/constants';
import { functions } from 'helpers/functions';
import QueryString from 'query-string';
import Truncate from 'v2/components/Shared/TruncateText';
import { useCreateConnectAction } from 'v2/hooks/useCreateConnectAction';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { connectService } from 'v2/services/connect';
import { run } from 'validations/ruleRunner';

import ProductSelectTable from './ProductSelectTable';
import Validations from './validations';
import { useActionState } from '../../contexts/ActionStateContext';
import { useConnectState } from '../../contexts/ConnectStateContext';
import { getOptionParametersQuery, getOptionsParametersShouldModify } from '../../helpers/actionOptionsHelpers';

const STEP_ITEMS = [{ title: 'Descrição' }, { title: 'Gatilho' }, { title: 'Ação' }, { title: 'Produtos' }];

const STEP = {
  DESCRIPTION: 0,
  TRIGGER: 1,
  ACTION: 2,
  PRODUCTS: 3,
  LOADING: 4,
  DONE: 5
} as const;

const NewAction: React.FC = () => {
  const { message } = App.useApp();
  const { currentConnect } = useConnectState();
  const {
    newAction,
    modalNewAction: modal,
    stepAction: step,
    optionsAction,
    setStepAction,
    previousStepAction: handleBack,
    setNewAction,
    closeNewActionModal,
    addOptionAction,
    resetOptionsAction,
    resetOptionAction
  } = useActionState();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const createActionMutation = useCreateConnectAction();
  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);

  const prevNewActionRef = useRef<any>(newAction);

  const errorFor = useCallback(
    (field: string) => (showErrors ? validationErrors[field] || '' : ''),
    [showErrors, validationErrors]
  );
  const hasError = useCallback(
    (field: string) => showErrors && !!validationErrors[field],
    [showErrors, validationErrors]
  );

  const handleChangeField = useCallback(
    (field: any, value: any) => {
      const updated = functions.cloneDeep(newAction);
      updated.fields = updated.fields || {};
      updated.fields[field] = value;
      updated.options_parameters = updated.fields;
      setNewAction(updated);
    },
    [setNewAction, newAction]
  );

  const handleChange = useCallback(
    (field: any, value: any) => {
      const updated = functions.cloneDeep(newAction);
      updated[field] = value;
      setNewAction(updated);
    },
    [setNewAction, newAction]
  );

  const dispatchNewOptionParam = useCallback(
    async (autoResponderId: number, connectId: number, itemName: string, params: any) => {
      const keys = itemName.split('_');
      const toSearch = `${keys[0]}_${keys[1]}`;

      let paramsString = undefined;

      if (params && typeof params === 'object') {
        paramsString = QueryString.stringify(params);
      }

      const result = await connectService.getActionFields(autoResponderId, connectId, toSearch, paramsString);
      const fieldData = result?.data ?? result;

      addOptionAction(itemName, functions.cloneDeep(fieldData));
    },
    [addOptionAction]
  );

  const fillOptionsParametersList = useCallback(
    (action: any, optionsParametersShouldModify: any) => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === action.option_id);

      if (!optionsParametersShouldModify || optionsParametersShouldModify.length === 0) {
        return;
      }

      if (!selectedOption || !selectedOption.parameters || !Array.isArray(selectedOption.parameters.campos)) {
        return;
      }

      Promise.all(
        selectedOption.parameters.campos.map(async (item: any) => {
          if (!optionsParametersShouldModify.includes(item.nome)) {
            return;
          }

          if (item.campo_carregar) {
            const params = getOptionParametersQuery(item, selectedOption, action);

            if (item.campo_carregar === 'tag') {
              item = { ...item, campo_carregar: undefined };
            }

            await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, params);
            return;
          }

          await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, undefined);
        })
      );
    },
    [options, currentConnect, dispatchNewOptionParam]
  );

  const isValidToNextStep = useCallback(() => {
    switch (step) {
      case STEP.DESCRIPTION:
        return !!newAction.description?.trim();
      case STEP.TRIGGER:
        return !!newAction.trigger_id;

      case STEP.ACTION: {
        const selectedOption =
          options && Array.isArray(options) && options.find((item: any) => item.id === newAction.option_id);

        if (!selectedOption) return false;

        if (selectedOption.parameters && Array.isArray(selectedOption.parameters.campos)) {
          return selectedOption.parameters.campos.every((item: any) => !!newAction.fields?.[item.nome]);
        }

        return !!newAction.option_id;
      }

      case STEP.PRODUCTS:
        return Array.isArray(newAction.products_id) && newAction.products_id.length > 0;
      default:
        return false;
    }
  }, [step, newAction, options]);

  const closeModal = useCallback(() => {
    setShowErrors(false);
    closeNewActionModal();
  }, [closeNewActionModal]);

  const handleNext = useCallback(() => {
    if (!isValidToNextStep()) {
      setShowErrors(true);
      if (step === STEP.DESCRIPTION) return;

      if (step === STEP.TRIGGER) {
        message.warning('Selecione um gatilho para continuar.');
        return;
      }

      if (step === STEP.ACTION) {
        message.warning('Preencha os campos da ação para continuar.');
        return;
      }

      if (step === STEP.PRODUCTS) {
        message.warning('Selecione ao menos um produto.');
        return;
      }

      return;
    }

    setShowErrors(false);
    setStepAction(step + 1);
  }, [isValidToNextStep, step, setStepAction, message]);

  const handleSaveNewAction = useCallback(async () => {
    if (!isValidToNextStep()) {
      message.warning('Selecione ao menos um produto.');
      return;
    }

    try {
      setStepAction(STEP.LOADING);
      const actionToSave = Object.assign({}, newAction);
      actionToSave.autoresponder_id = currentConnect.autoresponder_id;
      await createActionMutation.mutateAsync({ connectId: currentConnect.id, action: actionToSave });
      setStepAction(STEP.DONE);
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Não foi possível salvar a ação.');
      setStepAction(STEP.PRODUCTS);
    }
  }, [createActionMutation, newAction, currentConnect, setStepAction, message, isValidToNextStep]);

  const canFillOptionParam = useCallback(
    (name: string): boolean => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === newAction.option_id);
      const optionParam =
        selectedOption &&
        Array.isArray(selectedOption.parameters.campos) &&
        selectedOption.parameters.campos.find((item: any) => item.nome === name);

      const basicValidation = newAction && optionsAction && optionsAction[name] && Array.isArray(optionsAction[name]);

      if (basicValidation && !optionParam.campo_carregar) {
        return true;
      }

      const tags = name.split('_');
      const fieldKey = [tags[0] || '', optionParam.campo_carregar, tags[2] || '']
        .filter(value => value && value.length > 0)
        .join('_');

      const advancedValidation =
        newAction &&
        newAction.fields &&
        newAction.fields[fieldKey] &&
        optionsAction &&
        optionsAction[name] &&
        Array.isArray(optionsAction[name]);

      return advancedValidation;
    },
    [newAction, options, optionsAction]
  );

  useEffect(() => {
    const prevAction = prevNewActionRef.current;

    const optionHasUpdated = prevAction.option_id !== newAction.option_id;
    const actionOptionsHasUpdated = prevAction.options_parameters !== newAction.options_parameters || optionHasUpdated;
    const shouldUpdateOptionsParametersList =
      newAction && newAction.options_parameters && currentConnect && actionOptionsHasUpdated;

    if (shouldUpdateOptionsParametersList) {
      (async () => {
        if (optionHasUpdated) {
          resetOptionsAction();
        }

        const actionsParametersShouldModify = getOptionsParametersShouldModify(newAction, prevAction, options);

        actionsParametersShouldModify.forEach((item: any) => {
          resetOptionAction(item);
        });

        fillOptionsParametersList(newAction, actionsParametersShouldModify);

        setValidationErrors(run(newAction, Validations) as Record<string, string>);
      })();
    }

    prevNewActionRef.current = newAction;
  }, [newAction, currentConnect, options, resetOptionsAction, resetOptionAction, fillOptionsParametersList]);

  useEffect(() => {
    if (newAction) {
      setValidationErrors(run(newAction, Validations) as Record<string, string>);
    }
  }, [newAction]);

  useEffect(() => {
    if (!modal) {
      setShowErrors(false);
    }
  }, [modal]);

  const currentOptions = Array.isArray(options) ? options : [];
  const selectedOption = currentOptions.find((item: any) => item.id === newAction.option_id);
  const showFormFooter = step < STEP.LOADING;
  const isLastStep = step === STEP.PRODUCTS;

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      width={680}
      destroyOnClose
      title='Adicionar ação'
      footer={
        showFormFooter ? (
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={closeModal}>
              Cancelar
            </Button>
            {step !== STEP.DESCRIPTION && (
              <Button type='text' onClick={handleBack}>
                Voltar
              </Button>
            )}
            <Button
              type='primary'
              onClick={isLastStep ? handleSaveNewAction : handleNext}
              disabled={!isValidToNextStep()}
            >
              {isLastStep ? 'Salvar ação' : 'Avançar'}
            </Button>
          </div>
        ) : step === STEP.DONE ? (
          <div className='flex justify-end'>
            <Button type='primary' onClick={closeModal}>
              Concluir
            </Button>
          </div>
        ) : null
      }
    >
      {currentConnect && (
        <div className='mb-5 flex items-center gap-3 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
          {currentConnect.image && (
            <img
              src={currentConnect.image}
              alt={currentConnect?.name || 'Connect'}
              className='h-12 max-w-24 object-contain'
            />
          )}
          <div className='min-w-0'>
            <Typography.Text strong className='block'>
              {currentConnect?.name}
            </Typography.Text>
            <Typography.Text type='secondary' className='block truncate text-sm'>
              {currentConnect?.description}
            </Typography.Text>
            {currentConnect?.parameters?.[0] && (
              <>
                <Typography.Text strong className='mt-1 block text-sm'>
                  {currentConnect.parameters[0].label}
                </Typography.Text>
                <Typography.Text type='secondary' className='block text-sm'>
                  <Truncate count={50} final='...'>
                    {currentConnect.parameters[0].value}
                  </Truncate>
                </Typography.Text>
              </>
            )}
          </div>
        </div>
      )}

      {step <= STEP.PRODUCTS && (
        <Steps current={step} className='mb-6' size='small' items={STEP_ITEMS} responsive={false} />
      )}

      {step === STEP.DESCRIPTION && (
        <Form layout='vertical' className='flex flex-col gap-4'>
          <div className='flex items-center justify-between rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
            <div className='flex flex-col'>
              <Typography.Text strong>Status da ação</Typography.Text>
              <Typography.Text type='secondary' className='text-sm'>
                {newAction.active ? 'A ação ficará ativa após o cadastro' : 'A ação será criada inativa'}
              </Typography.Text>
            </div>
            <div className='flex items-center gap-2'>
              <Typography.Text type='secondary'>{newAction.active ? 'Ativada' : 'Desativada'}</Typography.Text>
              <Switch checked={newAction.active} onChange={checked => handleChange('active', checked)} />
            </div>
          </div>

          <Form.Item
            label='Título da ação'
            required
            validateStatus={hasError('description') ? 'error' : undefined}
            help={hasError('description') ? errorFor('description') : 'Identifique esta ação para encontrá-la depois'}
            className='mb-0'
          >
            <Input
              placeholder='Ex.: Enviar lead para lista de compradores'
              value={newAction.description}
              onChange={e => handleChange('description', e.target.value)}
              maxLength={120}
              showCount
            />
          </Form.Item>
        </Form>
      )}

      {step === STEP.TRIGGER && (
        <Form layout='vertical' className='flex flex-col gap-3'>
          <div>
            <Typography.Text strong className='block'>
              Quando isso acontecer
            </Typography.Text>
            <Typography.Text type='secondary'>Escolha o gatilho que dispara esta ação</Typography.Text>
          </div>
          <Form.Item
            label='Gatilho'
            required
            validateStatus={hasError('trigger_id') ? 'error' : undefined}
            help={hasError('trigger_id') ? errorFor('trigger_id') : undefined}
            className='mb-0'
          >
            <Select
              className='w-full'
              placeholder='Selecione um gatilho'
              value={newAction.trigger_id}
              onChange={value => handleChange('trigger_id', value)}
              options={
                triggers && Array.isArray(triggers)
                  ? triggers.map((item: any) => ({ value: item.id, label: item.name }))
                  : []
              }
            />
          </Form.Item>
        </Form>
      )}

      {step === STEP.ACTION && (
        <Form layout='vertical' className='flex flex-col gap-4'>
          <div>
            <Typography.Text strong className='block'>
              Faça isso
            </Typography.Text>
            <Typography.Text type='secondary'>Defina a ação executada no provedor</Typography.Text>
          </div>

          <Form.Item
            label='Ação'
            required
            validateStatus={hasError('option_id') ? 'error' : undefined}
            help={hasError('option_id') ? errorFor('option_id') : undefined}
            className='mb-0'
          >
            <Select
              className='w-full'
              placeholder='Selecione uma ação'
              value={newAction.option_id}
              onChange={value => handleChange('option_id', value)}
              disabled={!(options && Array.isArray(options))}
              options={currentOptions.map((option: any) => ({ value: option.id, label: option.title }))}
            />
          </Form.Item>

          {newAction.trigger_id &&
            newAction.option_id &&
            selectedOption &&
            selectedOption.parameters &&
            Array.isArray(selectedOption.parameters.campos) &&
            selectedOption.parameters.campos.map((item: any) => {
              return item.tipo === 'list' || item.tipo === 'listdinamico' ? (
                <Form.Item
                  key={`fieldAction-${item.nome}`}
                  label={item.label}
                  className='mb-0'
                  required
                  extra={
                    <>
                      {item.tooltip && <Typography.Text type='secondary'>{item.tooltip}</Typography.Text>}
                      {currentConnect?.autoresponder_id === constants.MAUTIC_AUTORESPONDER_ID && (
                        <Typography.Text type='secondary' className='mt-1 block text-xs'>
                          * Caso a listagem não esteja carregando, limpe o cache do seu Mautic ou procure ajuda na{' '}
                          <Typography.Link href='https://mautic.org/support' rel='noopener noreferrer' target='_blank'>
                            página de suporte do Mautic
                          </Typography.Link>
                          .
                        </Typography.Text>
                      )}
                    </>
                  }
                >
                  <Select
                    className='w-full'
                    value={newAction.fields[item.nome]}
                    onChange={value => handleChangeField(item.nome, value)}
                    disabled={!canFillOptionParam(item.nome)}
                    placeholder={!canFillOptionParam(item.nome) ? 'Carregando...' : 'Selecione'}
                    options={
                      optionsAction && Array.isArray(optionsAction[item.nome])
                        ? optionsAction[item.nome].map((opt: any) => ({ value: opt.id, label: opt.name }))
                        : []
                    }
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  key={`fieldAction-${item.nome}`}
                  label={item.label}
                  className='mb-0'
                  required
                  extra={item.tooltip ? <Typography.Text type='secondary'>{item.tooltip}</Typography.Text> : undefined}
                >
                  <Input
                    value={newAction.fields[item.nome]}
                    onChange={e => handleChangeField(item.nome, e.target.value)}
                    placeholder={item.label}
                  />
                </Form.Item>
              );
            })}
        </Form>
      )}

      {step === STEP.PRODUCTS && (
        <div className='flex flex-col gap-3'>
          <div>
            <Typography.Text strong className='block'>
              Produtos vinculados
            </Typography.Text>
            <Typography.Text type='secondary'>Selecione os produtos em que esta ação será aplicada</Typography.Text>
          </div>
          <ProductSelectTable />
        </div>
      )}

      {step === STEP.LOADING && (
        <div className='flex flex-col items-center justify-center gap-3 py-12'>
          <Spin size='large' />
          <Typography.Text type='secondary'>Salvando sua ação...</Typography.Text>
        </div>
      )}

      {step === STEP.DONE && (
        <Result
          status='success'
          title='Ação criada com sucesso'
          subTitle='A nova ação já está disponível na lista de ações desta integração.'
        />
      )}
    </Modal>
  );
};

export default NewAction;
